import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useAuth } from "../hooks/useAuth";

const TradingContext = createContext();
const STORAGE_KEY_PREFIX = "gainwise-trading-state-v3";
const BASE_PORTFOLIO_BALANCE = Number(import.meta.env.VITE_DEFAULT_TRADING_BALANCE || 0);
const REMOTE_STATE_ENDPOINTS = [
  (base) => `${base}/get-trading-state.php`,
  (base) => `${base}/update-trading-state.php`,
];
const REMOTE_BASE_CANDIDATES = [
  import.meta.env.VITE_BACKEND_API_BASE_URL,
  import.meta.env.VITE_AUTH_API_BASE_URL,
  "https://gainwisefx.com/api/",
  "/api",
].filter(Boolean);
const REMOTE_REFRESH_INTERVAL_MS = 5000;

export { TradingContext };

const DEFAULT_MARKETS = [
  { symbol: "BTCUSDT", displaySymbol: "BTC/USDT", name: "Bitcoin", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "ETHUSDT", displaySymbol: "ETH/USDT", name: "Ethereum", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "BNBUSDT", displaySymbol: "BNB/USDT", name: "BNB", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "SOLUSDT", displaySymbol: "SOL/USDT", name: "Solana", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "ADAUSDT", displaySymbol: "ADA/USDT", name: "Cardano", price: 0, change24h: 0, volume24h: 0 },
];

const ACTIONS = {
  SET_SELECTED_PAIR: "SET_SELECTED_PAIR",
  UPSERT_MARKET: "UPSERT_MARKET",
  BULK_MARKETS: "BULK_MARKETS",
  ADD_NOTIFICATION: "ADD_NOTIFICATION",
  REMOVE_NOTIFICATION: "REMOVE_NOTIFICATION",
  ADD_ORDER: "ADD_ORDER",
  UPDATE_ORDER: "UPDATE_ORDER",
  ADD_TRANSACTION: "ADD_TRANSACTION",
  UPDATE_TRANSACTION: "UPDATE_TRANSACTION",
  REMOVE_TRANSACTION: "REMOVE_TRANSACTION",
  CLEAR_TRANSACTIONS: "CLEAR_TRANSACTIONS",
  APPLY_COMPLETED_TRADE: "APPLY_COMPLETED_TRADE",
  SYNC_ACCOUNT_STATE: "SYNC_ACCOUNT_STATE",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  HYDRATE_STATE: "HYDRATE_STATE",
};

const createInitialState = () => ({
  isLoading: false,
  error: null,
  marketData: DEFAULT_MARKETS,
  selectedPair: DEFAULT_MARKETS[0],
  account: {
    startingBalance: BASE_PORTFOLIO_BALANCE,
    currentBalance: BASE_PORTFOLIO_BALANCE,
    equity: BASE_PORTFOLIO_BALANCE,
    availableBalance: BASE_PORTFOLIO_BALANCE,
    lockedBalance: 0,
    totalPnL: 0,
    realizedPnL: 0,
    totalInvested: 0,
    completedTrades: 0,
    winningTrades: 0,
    losses: 0,
    updatedAt: null,
  },
  portfolio: {
    totalValue: BASE_PORTFOLIO_BALANCE,
    totalPnL: 0,
    totalPnLPercent: 0,
    availableBalance: BASE_PORTFOLIO_BALANCE,
    totalInvested: 0,
    completedTrades: 0,
    winningTrades: 0,
  },
  transactions: [],
  orders: [],
  trades: [],
  notifications: [],
});

const initialState = createInitialState();

const getTradingStorageKey = (userId) => `${STORAGE_KEY_PREFIX}:${userId || "guest"}`;

const hydrateTradingState = (parsedState) => {
  const baseState = createInitialState();

  return {
    ...baseState,
    ...parsedState,
    account: {
      ...baseState.account,
      ...(parsedState.account || {}),
    },
    portfolio: {
      ...baseState.portfolio,
      ...(parsedState.portfolio || {}),
    },
    marketData: parsedState.marketData || baseState.marketData,
    selectedPair: parsedState.selectedPair || baseState.selectedPair,
    transactions: pruneTransactions(parsedState.transactions || []),
    orders: parsedState.orders || [],
    trades: parsedState.trades || [],
    notifications: parsedState.notifications || [],
  };
};

const loadStoredStateForUser = (userId) => {
  if (typeof window === "undefined" || !userId) {
    return createInitialState();
  }

  try {
    const savedState = window.localStorage.getItem(getTradingStorageKey(userId));
    if (!savedState) {
      return createInitialState();
    }

    const hydratedState = hydrateTradingState(JSON.parse(savedState));
    const baseState = createInitialState();

    return {
      ...hydratedState,
      account: baseState.account,
      portfolio: baseState.portfolio,
    };
  } catch {
    return createInitialState();
  }
};

const roundTo = (value, precision = 2) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Number(value.toFixed(precision));
};

const normalizeTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
};

const getTimestampValue = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const createAccountSignature = (account = {}) =>
  JSON.stringify({
    startingBalance: roundTo(account.startingBalance ?? 0),
    currentBalance: roundTo(account.currentBalance ?? 0),
    equity: roundTo(account.equity ?? 0),
    availableBalance: roundTo(account.availableBalance ?? 0),
    lockedBalance: roundTo(account.lockedBalance ?? 0),
    totalPnL: roundTo(account.totalPnL ?? account.totalProfitLoss ?? 0),
    realizedPnL: roundTo(account.realizedPnL ?? account.realizedProfitLoss ?? 0),
    totalInvested: roundTo(account.totalInvested ?? 0),
    completedTrades: Number(account.completedTrades ?? account.sessionsCompleted ?? 0),
    winningTrades: Number(account.winningTrades ?? account.wins ?? 0),
    losses: Number(account.losses ?? 0),
  });

const normalizeAccountSnapshot = (snapshot = {}) => ({
  startingBalance: roundTo(snapshot.startingBalance ?? BASE_PORTFOLIO_BALANCE),
  currentBalance: roundTo(snapshot.currentBalance ?? snapshot.equity ?? 0),
  equity: roundTo(snapshot.equity ?? snapshot.currentBalance ?? 0),
  availableBalance: roundTo(
    snapshot.availableBalance ?? snapshot.currentBalance ?? 0
  ),
  lockedBalance: roundTo(snapshot.lockedBalance ?? snapshot.activeTradingAmount ?? 0),
  totalPnL: roundTo(snapshot.totalProfitLoss ?? snapshot.totalPnL ?? 0),
  realizedPnL: roundTo(snapshot.realizedProfitLoss ?? snapshot.realizedPnL ?? 0),
  totalInvested: roundTo(snapshot.totalInvested ?? snapshot.activeTradingAmount ?? 0),
  completedTrades: snapshot.sessionsCompleted ?? snapshot.completedTrades ?? 0,
  winningTrades: snapshot.wins ?? snapshot.winningTrades ?? 0,
  losses: snapshot.losses ?? 0,
  updatedAt: normalizeTimestamp(snapshot.updatedAt) || new Date().toISOString(),
});

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const pruneTransactions = (transactions) => {
  const cutoff = Date.now() - THIRTY_DAYS_MS;
  return transactions.filter((transaction) => {
    const timestamp = new Date(transaction.timestamp || Date.now()).getTime();
    return Number.isFinite(timestamp) && timestamp >= cutoff;
  });
};

const upsertMarket = (markets, nextMarket) => {
  const index = markets.findIndex((market) => market.symbol === nextMarket.symbol);
  if (index === -1) {
    return [...markets, nextMarket];
  }

  return markets.map((market, marketIndex) =>
    marketIndex === index ? { ...market, ...nextMarket } : market
  );
};

const tradingReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_SELECTED_PAIR:
      return {
        ...state,
        selectedPair: action.payload,
      };

    case ACTIONS.UPSERT_MARKET: {
      const marketData = upsertMarket(state.marketData, action.payload);
      const selectedPair =
        state.selectedPair.symbol === action.payload.symbol
          ? { ...state.selectedPair, ...action.payload }
          : state.selectedPair;

      return {
        ...state,
        marketData,
        selectedPair,
      };
    }

    case ACTIONS.BULK_MARKETS:
      return {
        ...state,
        marketData: action.payload,
        selectedPair:
          action.payload.find((market) => market.symbol === state.selectedPair.symbol) ||
          action.payload[0] ||
          state.selectedPair,
      };

    case ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 100),
      };

    case ACTIONS.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload
        ),
      };

    case ACTIONS.ADD_ORDER:
      return {
        ...state,
        orders: [action.payload, ...state.orders],
      };

    case ACTIONS.UPDATE_ORDER:
      return {
        ...state,
        orders: state.orders.map((order) =>
          order.id === action.payload.id ? { ...order, ...action.payload } : order
        ),
      };

    case ACTIONS.ADD_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions([action.payload, ...state.transactions]),
      };

    case ACTIONS.UPDATE_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions(
          state.transactions.some(
            (transaction) => transaction.id === action.payload.id
          )
            ? state.transactions.map((transaction) =>
                transaction.id === action.payload.id
                  ? { ...transaction, ...action.payload }
                  : transaction
              )
            : [action.payload, ...state.transactions]
        ),
      };

    case ACTIONS.REMOVE_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions(
          state.transactions.filter(
            (transaction) => transaction.id !== action.payload
          )
        ),
      };

    case ACTIONS.CLEAR_TRANSACTIONS:
      return {
        ...state,
        transactions: [],
      };

    case ACTIONS.APPLY_COMPLETED_TRADE: {
      const { trade, profitIncrease, investedIncrease } = action.payload;
      const nextCompletedTrades = state.portfolio.completedTrades + 1;
      const nextWinningTrades =
        state.portfolio.winningTrades + (profitIncrease > 0 ? 1 : 0);
      const nextPnL = roundTo(state.portfolio.totalPnL + profitIncrease);
      const nextValue = roundTo(state.portfolio.totalValue + profitIncrease);
      const nextAvailable = roundTo(
        state.portfolio.availableBalance + profitIncrease
      );
      const nextInvested = roundTo(
        state.portfolio.totalInvested + investedIncrease
      );
      const nextPnLPercent =
        nextInvested > 0 ? roundTo((nextPnL / nextInvested) * 100) : 0;

      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          totalValue: nextValue,
          totalPnL: nextPnL,
          totalPnLPercent: nextPnLPercent,
          availableBalance: nextAvailable,
          totalInvested: nextInvested,
          completedTrades: nextCompletedTrades,
          winningTrades: nextWinningTrades,
        },
        trades: [trade, ...state.trades],
        transactions: pruneTransactions(
          state.transactions.some((transaction) => transaction.id === trade.id)
            ? state.transactions.map((transaction) =>
                transaction.id === trade.id ? { ...transaction, ...trade } : transaction
              )
            : [trade, ...state.transactions]
        ),
      };
    }

    case ACTIONS.SYNC_ACCOUNT_STATE: {
      const account = {
        ...state.account,
        ...action.payload,
      };
      const totalInvested = roundTo(account.totalInvested);
      const totalPnLPercent =
        totalInvested > 0 ? roundTo((account.totalPnL / totalInvested) * 100) : 0;

      return {
        ...state,
        account,
        portfolio: {
          ...state.portfolio,
          totalValue: roundTo(account.currentBalance),
          totalPnL: roundTo(account.totalPnL),
          totalPnLPercent,
          availableBalance: roundTo(account.availableBalance),
          totalInvested,
          completedTrades: account.completedTrades,
          winningTrades: account.winningTrades,
        },
      };
    }

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ACTIONS.HYDRATE_STATE:
      return hydrateTradingState(action.payload || {});

    default:
      return state;
  }
};

const createNotification = (payload) => ({
  id: crypto.randomUUID(),
  timestamp: new Date().toISOString(),
  ...payload,
});

const getStorageAreas = () => {
  if (typeof window === "undefined") {
    return [];
  }

  return [window.localStorage, window.sessionStorage].filter(Boolean);
};

const getStoredValue = (keys) => {
  for (const storage of getStorageAreas()) {
    for (const key of keys) {
      const value = storage.getItem(key);
      if (value) {
        return value;
      }
    }
  }

  return "";
};

const getStoredBackendUser = () => {
  try {
    const rawUser = getStoredValue(["trading_app_user", "userData"]);
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const getStoredAuthToken = () =>
  getStoredValue(["authToken", "token", "trading_app_token"]);

const createAuthHeaders = () => {
  const token = getStoredAuthToken();
  const headers = { "Content-Type": "application/json" };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["X-Auth-Token"] = token;
  }

  return headers;
};

const fetchRemoteTradingState = async () => {
  for (const base of REMOTE_BASE_CANDIDATES) {
    try {
      const response = await fetch(`${base}/get-trading-state.php`, {
        method: "GET",
        headers: createAuthHeaders(),
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      if (payload?.success) {
        return payload;
      }
    } catch {
      continue;
    }
  }

  return null;
};

const persistRemoteTradingState = async (account) => {
  for (const base of REMOTE_BASE_CANDIDATES) {
    try {
      const response = await fetch(`${base}/update-trading-state.php`, {
        method: "POST",
        headers: createAuthHeaders(),
        body: JSON.stringify({ account }),
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      if (payload?.success) {
        return payload;
      }
    } catch {
      continue;
    }
  }

  return null;
};

export const TradingProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [state, dispatch] = useReducer(tradingReducer, initialState);
  const hasLoadedRemoteState = useRef("");
  const lastPersistedSnapshot = useRef("");
  const hasCompletedInitialSync = useRef("");
  const lastKnownRemoteSignature = useRef("");
  const currentAccountRef = useRef(initialState.account);
  const persistInFlightRef = useRef(false);
  const pendingPersistRef = useRef(null);
  const lastScheduledSnapshotRef = useRef("");

  useEffect(() => {
    currentAccountRef.current = state.account;
  }, [state.account]);

  const flushPersistQueue = useCallback(async () => {
    if (persistInFlightRef.current) {
      return;
    }

    persistInFlightRef.current = true;

    while (pendingPersistRef.current) {
      const nextPersist = pendingPersistRef.current;
      pendingPersistRef.current = null;

      const payload = await persistRemoteTradingState(nextPersist.accountSnapshot);
      if (payload?.account) {
        lastPersistedSnapshot.current = nextPersist.serializedSnapshot;
        lastKnownRemoteSignature.current = createAccountSignature(payload.account);
        dispatch({
          type: ACTIONS.SYNC_ACCOUNT_STATE,
          payload: payload.account,
        });
      } else if (payload?.success) {
        lastPersistedSnapshot.current = nextPersist.serializedSnapshot;
        lastKnownRemoteSignature.current = createAccountSignature(nextPersist.accountSnapshot);
      } else {
        lastScheduledSnapshotRef.current = lastPersistedSnapshot.current;
      }
    }

    persistInFlightRef.current = false;
  }, []);

  const applyRemotePayload = useCallback(
    (payload, reason = "initial") => {
      if (!payload?.account || !user?.id) {
        return;
      }

      const remoteSignature = createAccountSignature(payload.account);
      dispatch({
        type: ACTIONS.SYNC_ACCOUNT_STATE,
        payload: payload.account,
      });
      lastKnownRemoteSignature.current = remoteSignature;
    },
    [user?.id]
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !user?.id ||
      !isAuthenticated ||
      hasCompletedInitialSync.current !== user.id
    ) {
      return;
    }

    window.localStorage.setItem(getTradingStorageKey(user.id), JSON.stringify(state));
  }, [isAuthenticated, state, user?.id]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated || !user?.id) {
      hasLoadedRemoteState.current = "";
      lastPersistedSnapshot.current = "";
      hasCompletedInitialSync.current = "";
      lastKnownRemoteSignature.current = "";
      lastScheduledSnapshotRef.current = "";
      pendingPersistRef.current = null;
      persistInFlightRef.current = false;
      dispatch({
        type: ACTIONS.HYDRATE_STATE,
        payload: createInitialState(),
      });
      return;
    }

    hasLoadedRemoteState.current = "";
    lastPersistedSnapshot.current = "";
    hasCompletedInitialSync.current = "";
    lastKnownRemoteSignature.current = "";
    lastScheduledSnapshotRef.current = "";
    pendingPersistRef.current = null;
    persistInFlightRef.current = false;
    dispatch({
      type: ACTIONS.HYDRATE_STATE,
      payload: loadStoredStateForUser(user.id),
    });
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
  }, [isAuthLoading, isAuthenticated, user?.id]);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || !user?.id) {
      return;
    }

    if (hasLoadedRemoteState.current === user.id) {
      return;
    }

    hasLoadedRemoteState.current = user.id;

    let isCancelled = false;

    fetchRemoteTradingState()
      .then((payload) => {
        if (!isCancelled) {
          applyRemotePayload(payload, "initial");
        }
      })
      .finally(() => {
        if (isCancelled) {
          return;
        }

        hasCompletedInitialSync.current = user.id;
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      });

    return () => {
      isCancelled = true;
      if (hasCompletedInitialSync.current !== user.id) {
        return;
      }
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    };
  }, [applyRemotePayload, isAuthLoading, isAuthenticated, state.account.currentBalance, user?.id]);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || !user?.id || hasCompletedInitialSync.current !== user.id) {
      return;
    }

    let cancelled = false;

    const refreshRemoteState = async () => {
      const payload = await fetchRemoteTradingState();
      if (cancelled || !payload?.account) {
        return;
      }

      const remoteSignature = createAccountSignature(payload.account);
      if (!lastKnownRemoteSignature.current) {
        lastKnownRemoteSignature.current = remoteSignature;
      }

      if (remoteSignature === lastKnownRemoteSignature.current) {
        return;
      }
      applyRemotePayload(payload, "poll");
    };

    const handleFocus = () => {
      refreshRemoteState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshRemoteState();
      }
    };

    const intervalId = window.setInterval(refreshRemoteState, REMOTE_REFRESH_INTERVAL_MS);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [applyRemotePayload, isAuthLoading, isAuthenticated, user?.id]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !user?.id ||
      state.isLoading ||
      hasCompletedInitialSync.current !== user.id
    ) {
      return;
    }

    const accountSnapshot = {
      startingBalance: roundTo(state.account.startingBalance),
      currentBalance: roundTo(state.account.currentBalance),
      equity: roundTo(state.account.equity),
      availableBalance: roundTo(state.account.availableBalance),
      lockedBalance: roundTo(state.account.lockedBalance),
      totalPnL: roundTo(state.account.totalPnL),
      realizedPnL: roundTo(state.account.realizedPnL),
      totalInvested: roundTo(state.account.totalInvested),
      completedTrades: state.account.completedTrades,
      winningTrades: state.account.winningTrades,
      losses: state.account.losses,
      updatedAt: normalizeTimestamp(state.account.updatedAt) || new Date().toISOString(),
    };

    const serializedSnapshot = JSON.stringify(accountSnapshot);
    if (
      lastPersistedSnapshot.current === serializedSnapshot ||
      lastScheduledSnapshotRef.current === serializedSnapshot
    ) {
      return;
    }

    lastScheduledSnapshotRef.current = serializedSnapshot;
    pendingPersistRef.current = {
      accountSnapshot,
      serializedSnapshot,
    };
    flushPersistQueue();
  }, [flushPersistQueue, isAuthenticated, state.account, state.isLoading, user?.id]);

  const setSelectedPair = useCallback((pair) => {
    dispatch({ type: ACTIONS.SET_SELECTED_PAIR, payload: pair });
  }, []);

  const upsertMarketData = useCallback((market) => {
    dispatch({ type: ACTIONS.UPSERT_MARKET, payload: market });
  }, []);

  const setMarketData = useCallback((markets) => {
    dispatch({ type: ACTIONS.BULK_MARKETS, payload: markets });
  }, []);

  const addNotification = useCallback((notification) => {
    dispatch({
      type: ACTIONS.ADD_NOTIFICATION,
      payload: createNotification(notification),
    });
  }, []);

  const removeNotification = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE_NOTIFICATION, payload: id });
  }, []);

  const addOrder = useCallback((order) => {
    dispatch({ type: ACTIONS.ADD_ORDER, payload: order });
  }, []);

  const updateOrder = useCallback((id, patch) => {
    dispatch({ type: ACTIONS.UPDATE_ORDER, payload: { id, ...patch } });
  }, []);

  const addTransaction = useCallback((transaction) => {
    dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: transaction });
  }, []);

  const updateTransaction = useCallback((id, patch) => {
    dispatch({ type: ACTIONS.UPDATE_TRANSACTION, payload: { id, ...patch } });
  }, []);

  const clearTransactions = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_TRANSACTIONS });
  }, []);

  const removeTransaction = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE_TRANSACTION, payload: id });
  }, []);

  const recordPendingTransaction = useCallback((transaction) => {
    const payload = {
      id: transaction.id || crypto.randomUUID(),
      status: "pending",
      timestamp: transaction.timestamp || new Date().toISOString(),
      ...transaction,
    };

    addTransaction(payload);
    return payload;
  }, [addTransaction]);

  const recordCompletedTrade = useCallback((tradeData) => {
    const trade = {
      id: tradeData.id || crypto.randomUUID(),
      timestamp: tradeData.timestamp || new Date().toISOString(),
      status: "completed",
      ...tradeData,
    };
    const realizedProfit = roundTo(Math.max(0, trade.profit || 0));
    const investedIncrease = roundTo(
      Math.max(0, trade.investedAmount || trade.notional || 0)
    );

    dispatch({
      type: ACTIONS.APPLY_COMPLETED_TRADE,
      payload: {
        trade,
        profitIncrease: realizedProfit,
        investedIncrease,
      },
    });

    dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: trade });
    return trade;
  }, []);

  const syncAccountState = useCallback((snapshot) => {
    const normalizedSnapshot = normalizeAccountSnapshot(snapshot);
    dispatch({
      type: ACTIONS.SYNC_ACCOUNT_STATE,
      payload: normalizedSnapshot,
    });
  }, []);

  const persistAccountState = useCallback(async (snapshot) => {
    const normalizedSnapshot = normalizeAccountSnapshot(snapshot);
    const serializedSnapshot = JSON.stringify(normalizedSnapshot);

    dispatch({
      type: ACTIONS.SYNC_ACCOUNT_STATE,
      payload: normalizedSnapshot,
    });

    lastScheduledSnapshotRef.current = serializedSnapshot;
    const payload = await persistRemoteTradingState(normalizedSnapshot);

    if (payload?.account) {
      const remoteSnapshot = normalizeAccountSnapshot(payload.account);
      const remoteSerializedSnapshot = JSON.stringify(remoteSnapshot);
      lastPersistedSnapshot.current = remoteSerializedSnapshot;
      lastScheduledSnapshotRef.current = remoteSerializedSnapshot;
      lastKnownRemoteSignature.current = createAccountSignature(remoteSnapshot);
      dispatch({
        type: ACTIONS.SYNC_ACCOUNT_STATE,
        payload: remoteSnapshot,
      });
      return remoteSnapshot;
    }

    lastScheduledSnapshotRef.current = lastPersistedSnapshot.current;
    return null;
  }, []);

  const setLoading = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: value });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  }, []);

  const setError = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: message });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setSelectedPair,
      upsertMarketData,
      setMarketData,
      addNotification,
      removeNotification,
      addOrder,
      updateOrder,
      addTransaction,
      updateTransaction,
      removeTransaction,
      clearTransactions,
      recordPendingTransaction,
      recordCompletedTrade,
      syncAccountState,
      persistAccountState,
      setLoading,
      clearError,
      setError,
      updatePortfolio: () => {},
      addTrade: recordCompletedTrade,
      updateTrade: updateTransaction,
      fetchMarketData: async () => state.marketData,
      fetchPortfolio: async () => state.portfolio,
      fetchBalance: async () => state.portfolio.availableBalance,
      fetchTrades: async () => state.trades,
      initializeTradingData: async () => state,
    }),
    [
      state,
      setSelectedPair,
      upsertMarketData,
      setMarketData,
      addNotification,
      removeNotification,
      addOrder,
      updateOrder,
      addTransaction,
      updateTransaction,
      removeTransaction,
      clearTransactions,
      recordPendingTransaction,
      recordCompletedTrade,
      syncAccountState,
      persistAccountState,
      setLoading,
      clearError,
      setError,
    ]
  );

  return (
    <TradingContext.Provider value={value}>{children}</TradingContext.Provider>
  );
};

export const useTrading = () => {
  const context = useContext(TradingContext);

  if (!context) {
    throw new Error("useTrading must be used within a TradingProvider");
  }

  return context;
};

export default TradingContext;
